/**
 * Imports the necessary dependencies for the ResetPasswordContainer component.
 * @module ResetPasswordContainer
 * @requires React
 * @requires useEffect
 * @requires useState
 * @requires Constant
 * @requires connect
 * @requires useNavigate
 * @requires ResetPasswordComponent
 */
import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import Constant from "../../../util/constant";

import { t } from "i18next";
import { resetPassword } from "../../../store/features/auth/authSlice";
import { showToast } from "../../../store/features/toast/toastSlice";
import ResetPasswordComponent from "./resetPasswordComponent";
/**
 * A container component for the password reset functionality.
 * @param {object} props - The component props.
 * @returns The rendered ResetPasswordComponent with the necessary props.
 */
const PasswordResetContainer = (props) => {
  /**
   * Initializes the necessary state variables for the navigation, visibility, and busy status.
   * Also defines an array of error fields for a multi-step form validation.
   * @param {function} useNavigate - The hook for navigating to different routes.
   * @returns None
   */
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [show, setShow] = useState(true);
  const [showconfirm, setShowConfirm] = useState(true);
  const [busy, setBusy] = useState(false);
  const multiErrorFields = [
    { length: t("MINIMUM_8_CHARACTERS") },
    { hasSpecialChar: t("MINIMUM_1_SPECIAL_CHARACTER") },
    { hasNumbers: t("MINIMUM_1_NUMERIC_CHARACTER") },
    { hasUppercase: t("MINIMUM_1_UPPERCASE_CHARACTER") },
    { hasLowerCase: t("MINIMUM_1_LOWWERCASE_CHARACTER") },
  ];
  const resetPass = window.sessionStorage.getItem(
    Constant.SESSIONSTORAGEKEYS.RESET_PASS
  );

  useEffect(() => {
    if (resetPass === "done") {
      navigate("/");
    }
  }, [resetPass, navigate]);

  useEffect(() => {
    localStorage.clear();
  }, []);

  /**
   * Toggles the value of the "show" state variable.
   * @returns None
   */
  const t_show = () => {
    setShow((prevShow) => !prevShow);
  };

  /**
   * Toggles the value of `showConfirm` state variable.
   * @returns None
   */
  const t_show2 = () => {
    setShowConfirm((prevShowConfirm) => !prevShowConfirm);
  };

  /**
   * A reset form object that defines the structure and validation rules for the form fields.
   * @property {{name: string, validate: object}} newpassword - The new password field, with validation rules.
   * @property {{name: string}} password - The password field.
   */
  const resetForm = {
    newpassword: {
      name: "newpassword",
      validate: {
        required: {
          value: true,
          message: t("PLEASE_ENTER_A_PASSWORD"),
        },
        validate: {
          hasUppercase: (value) =>
            value && value.match(Constant.REGEX.UPPERCASE) !== null,
          hasLowerCase: (value) =>
            value && value.match(Constant.REGEX.LOWERCASE) !== null,
          hasNumbers: (value) =>
            value && value.match(Constant.REGEX.NUMBER) !== null,
          hasSpecialChar: (value) =>
            value && value.match(Constant.REGEX.SPECIALCHARACTERS) !== null,
          length: (value) => (value && value.length >= 8) || "",
        },
      },
    },
    validate: {
      hasUppercase: (value) =>
        value && value.match(Constant.REGEX.UPPERCASE) !== null,
      hasLowerCase: (value) =>
        value && value.match(Constant.REGEX.LOWERCASE) !== null,
      hasNumbers: (value) =>
        value && value.match(Constant.REGEX.NUMBER) !== null,
      hasSpecialChar: (value) =>
        value && value.match(Constant.REGEX.SPECIALCHARACTERS) !== null,
      length: (value) => (value && value.length >= 8) || "",
    },
    password: {
      name: "password",
    },
  };

  /**
   * Handles the form submission event and performs an asynchronous operation to reset the admin password.
   * @param {any} value - The value submitted from the form.
   * @returns None
   */
  const onFormSubmit = async (value) => {
    setBusy(true);
    const resetToken = window.sessionStorage.getItem(
      Constant.SESSIONSTORAGEKEYS.RESET_TOKEN
    );
    const params = {
      //  email: email,
      resetToken: resetToken,
      password: value?.password,
    };

    dispatch(resetPassword(params)).then((resultAction) => {
      if (resetPassword.fulfilled.match(resultAction)) {
        setBusy(false);
        dispatch(
          showToast({
            show: false,
            message: resultAction?.payload?.message,
            type: "success",
          })
        );
        window.sessionStorage.setItem(
          Constant.SESSIONSTORAGEKEYS.RESET_PASS,
          "done"
        );
        navigate("/");
      } else if (resetPassword.rejected.match(resultAction)) {
        setBusy(false);
        dispatch(
          showToast({
            show: false,
            message: resultAction?.error?.message,
            type: "error",
          })
        );
      }
    });
  };

  const onClickCancel = () => {
    navigate("/forgot-password");
  };

  return (
    <ResetPasswordComponent
      onFormSubmit={onFormSubmit}
      resetForm={resetForm}
      t_show2={t_show2}
      t_show={t_show}
      multiErrorFields={multiErrorFields}
      busy={busy}
      showconfirm={showconfirm}
      show={show}
      onClickCancel={onClickCancel}
    />
  );
};

export default PasswordResetContainer;
